"use client";

import { SearchRequestError, getSearchResults } from "@/app/api/client-only";
import { initSearchToken } from "@/app/api/search-token";
import { SearchResult } from "@/types/journal";
import {
  SearchStateManager,
  cleanFilters,
  pickCollections,
} from "@/utils/search-state";
import { useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const DEFAULT_PAGE_SIZE = 50;

export interface FilterOptions {
  imprint?: string[];
  payment?: string[];
  licenses?: string[];
  publishing_model?: string[];
  collections?: string[];
  quality_score?: string[];
}

// Unified filter state that tracks both available and selected options
interface UnifiedFilterState {
  available: FilterOptions; // Available options from API + selected values
  selected: FilterOptions; // Currently selected filter values
}

/** Why the last search has no results to show; null when it succeeded. */
export type SearchError = "usage_exceeded" | "failed";

interface SearchContextType {
  slug: string;
  searchTerm: string | null;
  setSearchTerm: (term: string | null) => void;
  currentSearchTerm: string | null;
  filterState: UnifiedFilterState;
  searchResult: SearchResult | null;
  setSearchResult: (result: SearchResult | null) => void;
  loading: boolean;
  searchError: SearchError | null;
  search: (
    searchTerm: string,
    filters?: FilterOptions,
    options?: { source?: "autocomplete"; journalId?: string },
  ) => Promise<void>;
  showMore: () => void;
  toggleFilter: (key: keyof FilterOptions, value: string) => void;
  clearFilterCategory: (key: keyof FilterOptions) => void;
  clearAllFilters: () => void;
  clearAll: () => void;
  updateCollections: (collections: string[]) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({
  slug,
  initialToken = null,
  children,
}: {
  children: ReactNode;
  slug: string;
  /** Anonymous search token minted by the page render; null when not configured. */
  initialToken?: string | null;
}) {
  const searchParams = useSearchParams();
  const router = useRouter();

  // A layout effect, so it runs before every regular effect in the tree, including the
  // children's: the search box fetches autocomplete on mount for a ?term= deep link, and
  // React runs a child's effects before its parent's. Runs again with the new token after
  // router.refresh().
  useLayoutEffect(() => {
    initSearchToken(initialToken, () => router.refresh());
  }, [initialToken, router]);

  const [shouldLoad, setShouldLoad] = useState(!!searchParams.get("term"));

  const [searchTerm, setSearchTerm] = useState<string | null>(
    searchParams.get("term"),
  );
  const currentSearchTerm = searchParams.get("term");
  const [rawSearchResult, setRawSearchResult] = useState<SearchResult | null>(
    null,
  );
  const [displayedCount, setDisplayedCount] =
    useState<number>(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<SearchError | null>(null);
  // Numbers each search, so a slow response can't overwrite a newer search's outcome.
  const latestSearch = useRef(0);

  const searchResult = useMemo((): SearchResult | null => {
    if (!rawSearchResult) return null;

    return {
      ...rawSearchResult,
      data: rawSearchResult.data.slice(0, displayedCount),
      has_more: displayedCount < rawSearchResult.data.length,
    };
  }, [rawSearchResult, displayedCount]);

  const setSearchResult = useCallback((result: SearchResult | null) => {
    setRawSearchResult(result);
    setDisplayedCount(DEFAULT_PAGE_SIZE);
  }, []);

  const showMore = useCallback(() => {
    setDisplayedCount((prev) => prev + DEFAULT_PAGE_SIZE);
  }, []);

  // Initialize unified filter state from URL
  const [stateManager] = useState(() => new SearchStateManager(searchParams));
  const initialFilters = stateManager.getState().filters;

  const [filterState, setFilterState] = useState<UnifiedFilterState>({
    available: {},
    selected: cleanFilters(initialFilters),
  });

  // Centralized URL update function
  const updateURL = useCallback(
    (term: string | undefined, filters: FilterOptions) => {
      stateManager.setTerm(term).setFilters(cleanFilters(filters));
      const url = stateManager.toURL(`/${slug}`);
      window.history.replaceState({}, "", url);
    },
    [slug, stateManager],
  );

  // Simplified search function with optional filter override
  const search = useCallback(
    async (
      searchTerm: string,
      filters?: FilterOptions,
      options?: { source?: "autocomplete"; journalId?: string },
    ) => {
      const searchId = ++latestSearch.current;
      setLoading(true);
      setSearchError(null);

      const effectiveFilters = filters ?? filterState.selected;

      let result: SearchResult | null = null;
      let failure: SearchError | null = null;
      try {
        result = await getSearchResults(
          slug,
          searchTerm,
          effectiveFilters,
          options,
        );
      } catch (error) {
        console.error(error);
        failure =
          error instanceof SearchRequestError && error.status === 429
            ? "usage_exceeded"
            : "failed";
      }
      // A newer search started while this one was in flight; its outcome wins.
      if (searchId !== latestSearch.current) return;
      setSearchError(failure);

      // Update available filters by merging API results with selected values
      if (result?.filters) {
        const {
          imprints = [],
          payments = [],
          licenses = [],
          publishing_models = [],
        } = result.filters;

        setFilterState((prev) => {
          const levelOrder = ["3", "2", "1", "X", "0", "null"];
          const flatQualityScores = Object.keys(result.filters)
            .filter((key) => key.startsWith("quality_scores_"))
            .flatMap((key) => {
              const system = key.replace("quality_scores_", "");
              return result.filters[key].map((v) => `${system}:${v ?? "null"}`);
            });

          const newAvailable: FilterOptions = {
            // Merge API options with currently selected to ensure selected are always visible
            imprint: Array.from(
              new Set([...imprints, ...(effectiveFilters.imprint || [])]),
            ).sort(),
            payment: Array.from(
              new Set([...payments, ...(effectiveFilters.payment || [])]),
            ).sort(),
            licenses: Array.from(
              new Set([...licenses, ...(effectiveFilters.licenses || [])]),
            ).sort(),
            publishing_model: Array.from(
              new Set([
                ...publishing_models,
                ...(effectiveFilters.publishing_model || []),
              ]),
            ).sort(),
            quality_score: Array.from(
              new Set([
                ...flatQualityScores,
                ...(effectiveFilters.quality_score || []),
              ]),
            ).sort((a, b) => {
              const [sysA, valA] = a.split(":");
              const [sysB, valB] = b.split(":");
              if (sysA !== sysB) return sysA.localeCompare(sysB);
              const idxA = levelOrder.indexOf(valA);
              const idxB = levelOrder.indexOf(valB);
              return (idxA === -1 ? levelOrder.length : idxA) - (idxB === -1 ? levelOrder.length : idxB);
            }),
            // Collections come from branding, preserve them if they exist
            collections:
              prev.available.collections || effectiveFilters.collections,
          };

          return {
            available: cleanFilters(newAvailable),
            selected: effectiveFilters, // Use the filters that were actually searched
          };
        });
      } else {
        // Even when no filters returned, update selected state
        setFilterState((prev) => ({
          ...prev,
          selected: effectiveFilters,
        }));
      }

      setSearchResult(result);

      setLoading(false);
    },
    [slug, filterState.selected, setSearchResult],
  );

  // Simplified filter toggle
  const toggleFilter = useCallback(
    (key: keyof FilterOptions, value: string) => {
      const currentState = filterState;
      const currentValues = currentState.selected[key] || [];
      const index = currentValues.indexOf(value);

      let newValues: string[];
      if (index >= 0) {
        // Remove value
        newValues = currentValues.filter((v) => v !== value);
      } else {
        // Add value
        newValues = [...currentValues, value];
      }

      // Check if all available options are selected (treat as none selected)
      const availableOptions = currentState.available[key];
      if (
        availableOptions &&
        newValues.length === availableOptions.length &&
        availableOptions.every((opt) => newValues.includes(opt))
      ) {
        newValues = [];
      }

      const newSelected = { ...currentState.selected };
      if (newValues.length > 0) {
        newSelected[key] = newValues;
      } else {
        delete newSelected[key];
      }

      // Update URL with new filters
      updateURL(currentSearchTerm || undefined, newSelected);

      // Trigger search with updated filters - pass filters directly
      if (currentSearchTerm) {
        search(currentSearchTerm, newSelected);
      }
    },
    [currentSearchTerm, filterState, search, updateURL],
  );

  // Clear specific filter category
  const clearFilterCategory = useCallback(
    (key: keyof FilterOptions) => {
      const newSelected = { ...filterState.selected };
      delete newSelected[key];

      updateURL(currentSearchTerm || undefined, newSelected);

      if (currentSearchTerm) {
        search(currentSearchTerm, newSelected);
      }
    },
    [currentSearchTerm, filterState.selected, search, updateURL],
  );

  // Clear all filters (collections is a special filter and is preserved)
  const clearAllFilters = useCallback(() => {
    const remaining = pickCollections(filterState.selected);

    updateURL(currentSearchTerm || undefined, remaining);

    if (currentSearchTerm) {
      search(currentSearchTerm, remaining);
    }
  }, [currentSearchTerm, filterState.selected, search, updateURL]);

  // Clear everything (search term, filters, and results) without triggering a new search
  const clearAll = useCallback(() => {
    latestSearch.current++; // drop any search still in flight
    setLoading(false); // the dropped search won't get to turn the spinner off
    setSearchResult(null);
    setSearchError(null);
    setSearchTerm(null);
    setFilterState((prev) => ({
      ...prev,
      selected: {},
    }));
    window.history.replaceState({}, "", `/${slug}`);
  }, [slug, setSearchResult]);

  // Update collections (unified with other filters)
  const updateCollections = useCallback(
    (collections: string[]) => {
      const newSelected = { ...filterState.selected };

      if (collections.length > 0) {
        newSelected.collections = collections;
      } else {
        delete newSelected.collections;
      }
      setFilterState((prev) => ({
        ...prev,
        selected: {
          ...prev.selected,
          collections,
        },
      }));
      updateURL(currentSearchTerm || undefined, newSelected);

      // Only search if there's a current search term
      if (currentSearchTerm) {
        search(currentSearchTerm, newSelected);
      }
    },
    [currentSearchTerm, filterState.selected, search, updateURL],
  );

  // Load search results when the search term is populated on page load
  useEffect(() => {
    if (shouldLoad && searchTerm) {
      search(searchTerm);
      setShouldLoad(false);
    }
  }, [shouldLoad, searchTerm, search]);

  return (
    <SearchContext.Provider
      value={{
        slug,
        searchTerm,
        setSearchTerm,
        currentSearchTerm,
        filterState,
        searchResult,
        setSearchResult,
        loading,
        searchError,
        search,
        showMore,
        toggleFilter,
        clearFilterCategory,
        clearAllFilters,
        clearAll,
        updateCollections,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (undefined === context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
}
